import { CheckOutlined } from '@ant-design/icons'
import { ApiError } from '@community_dev/requests'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation } from '@tanstack/react-query'
import { Button, Input, Space, Spin, notification } from 'antd'
import React, { useRef, useState } from 'react'
import styled, { css } from 'styled-components'

import { ButtonModalPrompt } from 'components/ButtonModalPrompt'

enum InlineEditFieldMode {
  VIEW = 'VIEW',
  EDIT = 'EDIT',
}

const StyledEditButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme?.COLORS?.LINKS};
  }
`

const StyledProcessing = styled.span`
  margin-right: 8px;
`

const StyledInputWrapper = styled.div<{ $valid: boolean }>`
  display: flex;
  ${({ $valid }) =>
    !$valid &&
    css`
      input[type='text']:focus,
      input[type='text'] {
        border: 1px solid red;
      }
    `}
`

type InlineEditFieldProps = {
  children?: (props: { valid: boolean }) => React.ReactNode
  disabled?: boolean
  defaultValue?: string | number
  name?: string
  placeholder?: string
  prepare?(value: string): unknown
  pattern?: RegExp
  normalize?(value: string): string
  onSuccess?(data: unknown): void
  updateEntity(variables: unknown): Promise<unknown>
  useErrorMessage?: boolean
  validation?(value: string): boolean
  warningModalText?: string
}

export function InlineEditField({
  children = () => null,
  defaultValue = '',
  disabled = false,
  name,
  normalize,
  onSuccess,
  pattern,
  placeholder = '',
  prepare,
  updateEntity,
  useErrorMessage = false,
  validation,
  warningModalText,
}: InlineEditFieldProps) {
  const inputRef = useRef(undefined)
  const [state, setState] = useState<InlineEditFieldMode>(InlineEditFieldMode.VIEW)
  const [value, setValue] = useState(defaultValue?.toString())
  const [valid, setValid] = useState(true)
  const { mutate: save, isLoading } = useMutation(updateEntity, {
    onError(e: ApiError) {
      const suffix = useErrorMessage && e.errors?.[0]?.message ? `: ${e.errors?.[0]?.message}` : '. Please try again.'
      notification.error({
        message: name ? `${name} failed to save${suffix}` : `Sorry, something went wrong${suffix}`,
        placement: 'bottomRight',
        duration: 2,
      })
      setValue(defaultValue.toString())
      setState(InlineEditFieldMode.VIEW)
    },
    onSuccess(data) {
      notification.success({
        message: name ? `${name} saved.` : 'Saved.',
        placement: 'bottomRight',
        duration: 2,
      })
      if (onSuccess) onSuccess(data)
      setState(InlineEditFieldMode.VIEW)
    },
  })

  function handleChange(evt: any) {
    const current = evt.target.value
    if (pattern && !current.match(pattern)) return
    setValue(current)
  }

  function handleKeyDown(evt: any) {
    // enter is handled by onPressEnter from andt Input
    if (evt.key === 'Enter') {
      return
    }

    if (evt.key !== 'Escape') return
    setValue(defaultValue.toString())
    setState(InlineEditFieldMode.VIEW)
  }

  function handleSave() {
    const nextValue = normalize ? normalize(value) : value
    if (validation && !validation(nextValue)) {
      setValid(false)
      return
    }
    setValid(true)
    setValue(nextValue)
    const data = prepare ? prepare(nextValue) : nextValue
    save(data)
  }

  if (isLoading) {
    return (
      <div aria-busy aria-label="Saving" aria-live="polite">
        <StyledProcessing>{value}</StyledProcessing> <Spin size="small" />
      </div>
    )
  }

  switch (state) {
    case InlineEditFieldMode.EDIT:
      return (
        <>
          <div>
            <Space>
              <StyledInputWrapper $valid={valid}>
                <Input
                  autoFocus
                  disabled={disabled}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  onPressEnter={handleSave}
                  placeholder={placeholder}
                  // @ts-expect-error TS(2322): Type 'MutableRefObject<undefined>' is not assignab... Remove this comment to see the full error message
                  ref={inputRef}
                  value={value?.replace(/,/g, '')}
                />
                {warningModalText ? (
                  <ButtonModalPrompt
                    buttonText={<CheckOutlined />}
                    buttonType="primary"
                    modalText={warningModalText}
                    modalTitle="Warning"
                    onClick={handleSave}
                    size="middle"
                  />
                ) : (
                  <Button aria-label="Save" onClick={handleSave} size="middle" type="primary">
                    <CheckOutlined />
                  </Button>
                )}
              </StyledInputWrapper>
            </Space>
          </div>
          {children({ valid })}
        </>
      )
    default:
      return (
        <>
          <div>
            {value}{' '}
            {!disabled && (
              <StyledEditButton aria-label="Edit" onClick={() => setState(InlineEditFieldMode.EDIT)}>
                <FontAwesomeIcon icon="edit" />
              </StyledEditButton>
            )}
          </div>
          {children({ valid })}
        </>
      )
  }
}
